import TransformRegion from './transform-region.js';

export default class RippleTransforms {
	constructor(ctx) {
		const _ = this;
		_.ctx = ctx;

		// Cache values to be updated in reInit
		_.paddingLeftWidth = 0;
		_.slideMinWidth = 0;

		// set up handler functions to bind and unbind later
		_.handlers = {
			windowResize: () => _.reInit(),
			optionsChanged: () => _.reInit(),
		};

		// left ranges
		_.leftEndToMin = new TransformRegion({
			// name: 'left end to min',
			property: '_leftWidthTrimmed',
		});

		_.leftMinToSquished = new TransformRegion({
			// name: 'left min to squished',
			property: '_leftWidthTrimmed',
		});

		_.leftSquishedToFull = new TransformRegion({
			// name: 'left squished to full',
			property: '_leftWidthTrimmed',
		});

		// Left Masked on exit
		_.leftFullToMin = new TransformRegion({
			// name: 'left full to min',
			property: '_leftWidthTrimmed',
		});

		_.leftFullToMinEnd = new TransformRegion({
			// name: 'left full to min end',
			property: '_leftWidthTrimmed',
		});

		// right transform ranges
		_.rightEndToMin = new TransformRegion({
			name: 'right width end',
			property: '_rightWidthTrimmed',
		});

		_.rightMinToSquished = new TransformRegion({
			// name: 'right min to squished',
			property: '_rightWidthTrimmed',
		});

		_.rightSquishedToFull = new TransformRegion({
			// name: 'right squished to full',
			property: '_rightWidthTrimmed',
		});

		// Right Masked on exit
		_.rightFullToMin = new TransformRegion({
			// name: 'right full to min',
			property: '_rightWidthTrimmed',
		});

		_.rightFullToMinEnd = new TransformRegion({
			// name: 'right full to min end',
			property: '_rightWidthTrimmed',
		});
	}

	init() {
		this.bindEvents();
		this.reInit();
	}

	reInit() {
		const _ = this;
		const widths = _.ctx.store.getWidths();
		
		_.viewportWidth = widths.viewport;
		_.paddingLeftWidth = widths.paddingLeft;
		_.paddingRightWidth = widths.paddingRight;
		_.slideWidth = widths.slide;
		_.gapWidth = widths.gap;
		_.slideMinWidth = widths.slideMin;
		_.trackWidth = widths.track;
		_.shiftAmount = widths.slideMin + widths.gap;

		_.calculateTransformPoints();
	}

	bindEvents() {
		const _ = this;
		_.ctx.emitter.on(_.ctx.events.window.resize, _.handlers.windowResize);
		_.ctx.emitter.on(_.ctx.events.store.optionsChanged, _.handlers.optionsChanged);
	}

	calculateTransformPoints() {
		const _ = this;

		const halfSlideWidth = _.slideWidth / 2;
		const slideWidth = _.slideWidth;
		const slideAndGapWidth = _.slideWidth + _.gapWidth;
		const slideMinWidth = _.slideMinWidth;

		const minSize = (slideWidth - slideMinWidth) * -1;
		const removedAmount = (_.gapWidth + slideMinWidth) * -1;

		// calculate points for left side
		const pointL2 = halfSlideWidth + _.paddingLeftWidth;
		const pointL1 = pointL2 - slideAndGapWidth;
		const pointLMaskOut = pointL2 - slideWidth + slideMinWidth;
		const pointL3 = pointL2 + slideAndGapWidth;

		// Left transition in
		_.leftEndToMin.updateOptions({
			startPos: pointL1 - _.trackWidth,
			endPos: pointL1,
			startValue: minSize,
			endValue: minSize,
		});

		_.leftMinToSquished.updateOptions({
			startPos: pointL1,
			endPos: pointL2,
			startValue: minSize,
			endValue: removedAmount,
		});

		_.leftSquishedToFull.updateOptions({
			startPos: pointL2,
			endPos: pointL3,
			startValue: removedAmount,
			endValue: 0,
		});

		// Left masked
		_.leftFullToMin.updateOptions({
			startPos: pointLMaskOut,
			endPos: pointL2,
			startValue: minSize,
			endValue: 0,
		});

		_.leftFullToMinEnd.updateOptions({
			startPos: pointLMaskOut - _.trackWidth,
			endPos: pointLMaskOut,
			startValue: minSize,
			endValue: minSize,
		});

		// calculate points for right side
		const pointR2 = _.viewportWidth - halfSlideWidth - _.paddingRightWidth;
		const pointR1 = pointR2 - slideAndGapWidth;
		const pointR3 = pointR2 + slideAndGapWidth;
		const pointR4 = pointR3 + _.trackWidth;

		const pointRMaskOut = pointR2 + slideWidth - slideMinWidth;

		const shiftNegative = _.shiftAmount * -1;

		_.rightSquishedToFull.updateOptions({
			startPos: pointR2,
			endPos: pointR1,
			startValue: removedAmount,
			endValue: 0,
		});

		_.rightMinToSquished.updateOptions({
			startPos: pointR3,
			endPos: pointR2,
			startValue: minSize,
			endValue: removedAmount,
		});

		_.rightEndToMin.updateOptions({
			startPos: pointR4,
			endPos: pointR3,
			startValue: minSize,
			endValue: minSize,
		});

		_.rightFullToMin.updateOptions({
			startPos: pointRMaskOut,
			endPos: pointR2,
			startValue: minSize,
			endValue: 0,
		});

		_.rightFullToMinEnd.updateOptions({
			startPos: pointRMaskOut + _.trackWidth,
			endPos: pointRMaskOut,
			startValue: minSize,
			endValue: minSize,
		});
	}

	// Apply transforms to calculate width adjustments for ripple effect
	applyTransforms(slide, trackPos, percent, frame) {
		const _ = this;

		// apply left transforms
		_.leftEndToMin.applyTransform(slide, percent, trackPos);
		_.leftMinToSquished.applyTransform(slide, percent, trackPos);
		_.leftSquishedToFull.applyTransform(slide, percent, trackPos);

		_.leftFullToMin.applyTransform(slide, 1 - percent, trackPos);
		_.leftFullToMinEnd.applyTransform(slide, 1 - percent, trackPos);

		// apply right transforms
		_.rightEndToMin.applyTransform(slide, 1 - percent, trackPos);
		_.rightMinToSquished.applyTransform(slide, 1 - percent, trackPos);
		_.rightSquishedToFull.applyTransform(slide, 1 - percent, trackPos);

		_.rightFullToMin.applyTransform(slide, percent, trackPos);
		_.rightFullToMinEnd.applyTransform(slide, percent, trackPos);
		
		// Convert negative trim values to positive (width reduction)
		if (slide._leftWidthTrimmed < 0) slide._leftWidthTrimmed = Math.abs(slide._leftWidthTrimmed);
		if (slide._rightWidthTrimmed < 0) slide._rightWidthTrimmed = Math.abs(slide._rightWidthTrimmed);
	}

	// clean up our mess
	destroy() {
		const _ = this;

		// unbind events
		_.ctx.emitter.off(_.ctx.events.window.resize, _.handlers.windowResize);
		_.ctx.emitter.off(_.ctx.events.store.optionsChanged, _.handlers.optionsChanged);

		// if transform regions require cleanup, call their destroy methods if available
		_.leftEndToMin?.destroy();
		_.leftMinToSquished?.destroy();
		_.leftSquishedToFull?.destroy();
		_.leftFullToMin?.destroy();
		_.leftFullToMinEnd?.destroy();
		_.rightEndToMin?.destroy();
		_.rightMinToSquished?.destroy();
		_.rightSquishedToFull?.destroy();
		_.rightFullToMin?.destroy();
		_.rightFullToMinEnd?.destroy();

		// clear references
		_.ctx = null;
		_.handlers = null;
	}
}
